// Contratar (S10; diseño §10): una sola ventana. Lo que necesitas contado como a una persona, lo que el
// agente necesitará saber (para qué y cómo sabrás que está bien), quién lo hace (modelo y potencia: los
// elige Pau) y, plegado, lo opcional. El puesto NO se elige aquí: lo elige el agente en su plan y Pau lo
// aprueba con el plan. Las reglas viven en reglas.ts; aquí solo se pinta y se envía.
import { ChevronDown, Paperclip, UserPlus, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useToastStore } from '../../../stores/toast.store';
import { uploadAttachment } from '../../../lib/api';
import { sendAgentInput } from '../../../hooks/useSocket';
import { oficinaApi } from '../api';
import { MODELOS } from '../compartido/modelos';
import { LIMITES, POTENCIA_EN_CRISTIANO, POTENCIAS, type Potencia } from '../compartido/vocabulario';
import { useOficinaStore } from '../store';
import { mensajeError, respondioElServidor } from '../errores';
import { AreaTexto, Aviso, Boton, Campo, Casilla, Etiqueta, Selector, Ventana } from '../ui';
import { aFormulario, necesidades, pareceSecreto, type BorradorContratar } from './reglas';
import { quien } from '../marco/estado';

const CLAVE_BORRADOR = 'oficina:borrador-contratar';
/** Como en el chat: capturas y documentos, hasta 10 por encargo. */
const MAX_ADJUNTOS = 10;

function vacio(salaId: string, modelo: string, potencia: Potencia): BorradorContratar {
  return { caja: '', salaId, modelo, potencia, resultado: '', paraQue: '', comoSabre: '', noLoSe: false, noTocar: '', nombre: '', tema: '' };
}

/**
 * `alAbrirAgente`: 09-10 (Pau aprobó la maqueta «tras contratar, te lleva al agente») — el aviso de «Ha llegado» trae
 * «Ver», que abre su chat (lo mismo que pulsarlo en la oficina). Sin ella (maquetas), el aviso va sin botón.
 */
export function VentanaContratar({ alAbrirAgente }: { alAbrirAgente?: (agentId: string) => void } = {}) {
  const { contratarAbierto, salaParaContratar, encargoParaContratar, cerrarContratar, salas } = useOficinaStore();
  const principal = salas.find((s) => s.principal)?.projectId ?? salas[0]?.projectId ?? '';
  const [b, setB] = useState<BorradorContratar>(() => vacio(principal, MODELOS[0]!.id, 'medium'));
  const [detalles, setDetalles] = useState(false);
  const [enviando, setEnviando] = useState(false);
  // 29-09 («cuando contrato no me deja añadir fotos»): capturas y archivos del encargo. Se guardan aquí y, al
  // contratar, se suben al agente y le llegan en el mensaje siguiente al encargo (el encargo se lo avisa).
  const [archivos, setArchivos] = useState<Array<{ id: string; file: File; vista: string | null }>>([]);
  const elegir = useRef<HTMLInputElement>(null);
  const anadir = (fs: File[]) =>
    setArchivos((xs) =>
      [...xs, ...fs.map((file) => ({ id: crypto.randomUUID(), file, vista: file.type.startsWith('image/') ? URL.createObjectURL(file) : null }))].slice(0, MAX_ADJUNTOS),
    );
  const quitar = (id: string) =>
    setArchivos((xs) => {
      const a = xs.find((x) => x.id === id);
      if (a?.vista) URL.revokeObjectURL(a.vista);
      return xs.filter((x) => x.id !== id);
    });
  const [error, setError] = useState<string | null>(null);
  const clave = useRef<string>(crypto.randomUUID());

  // Al abrir: el borrador guardado o, si no hay, uno nuevo con la preferencia de modelo de Pau.
  useEffect(() => {
    if (!contratarAbierto) return;
    clave.current = crypto.randomUUID();
    setError(null);
    setArchivos((xs) => {
      for (const x of xs) if (x.vista) URL.revokeObjectURL(x.vista);
      return [];
    });
    // Encargo ya escrito (segunda opinión de un despliegue): manda sobre el borrador guardado.
    if (encargoParaContratar) {
      setB({ ...vacio(salaParaContratar ?? principal, MODELOS[0]!.id, 'medium'), caja: encargoParaContratar });
      return;
    }
    const guardado = localStorage.getItem(CLAVE_BORRADOR);
    if (guardado) {
      try {
        setB({ ...vacio(principal, MODELOS[0]!.id, 'medium'), ...(JSON.parse(guardado) as Partial<BorradorContratar>), ...(salaParaContratar ? { salaId: salaParaContratar } : {}) });
        return;
      } catch {
        localStorage.removeItem(CLAVE_BORRADOR);
      }
    }
    // 30-09 (Pau: «en el pop-up de contratación se me queda el texto que había puesto antes»): sin borrador, la
    // ventana abre VACÍA en el acto; la preferencia de modelo llega después y solo toca modelo y potencia. Antes
    // se quedaba con el encargo anterior hasta que llegaba la preferencia, y el guardado automático lo volvía a
    // escribir como borrador (que reaparecía en la siguiente contratación).
    const inicial = vacio(salaParaContratar ?? principal, MODELOS[0]!.id, 'medium');
    setB(inicial);
    const esta = clave.current;
    void oficinaApi
      .preferencias()
      // Solo si sigue siendo esta apertura y Pau no ha elegido ya otro modelo o potencia.
      .then((p) => setB((x) => (clave.current !== esta || x.modelo !== inicial.modelo || x.potencia !== inicial.potencia ? x : { ...x, modelo: p.modelo, potencia: p.potencia })))
      .catch(() => undefined);
  }, [contratarAbierto, salaParaContratar, encargoParaContratar, principal]);

  const n = useMemo(() => necesidades(b), [b]);
  const secreto = pareceSecreto(`${b.caja} ${b.paraQue} ${b.comoSabre} ${b.resultado}`);
  // El borrador se guarda solo con lo que escribe Pau (nunca desde el estado que queda al cerrar o contratar).
  const cambia = <K extends keyof BorradorContratar>(k: K, v: BorradorContratar[K]) =>
    setB((x) => {
      const nuevo = { ...x, [k]: v };
      if (nuevo.caja.trim()) localStorage.setItem(CLAVE_BORRADOR, JSON.stringify(nuevo));
      else localStorage.removeItem(CLAVE_BORRADOR);
      return nuevo;
    });
  const puede = n.puedeContratar && !secreto && !enviando;

  function cerrar() {
    if (b.caja.trim() && !window.confirm(t('oficinaV2.contratarVentana.descartar'))) return;
    localStorage.removeItem(CLAVE_BORRADOR);
    setB(vacio(principal, b.modelo, b.potencia));
    cerrarContratar();
  }

  async function contratar() {
    if (!puede) return;
    setEnviando(true);
    setError(null);
    try {
      const { contratado } = await oficinaApi.contratar({ ...aFormulario(b), adjuntosPorLlegar: archivos.length }, clave.current);
      localStorage.removeItem(CLAVE_BORRADOR);
      setB(vacio(principal, b.modelo, b.potencia));
      cerrarContratar();
      if (archivos.length) void mandarAdjuntos(contratado.agentId, archivos.map((a) => a.file));
      setArchivos([]);
      const sala = salas.find((s) => s.projectId === contratado.projectId)?.nombre ?? '';
      // El id llega con la respuesta: el chat se abre aunque el aviso del socket con su ficha aún no haya llegado
      // (la ventana del chat lo espera y se rellena sola al llegar).
      useToastStore.getState().push({
        message: t('oficinaV2.contratarVentana.llegado', { nombre: quien(contratado), sala }),
        ...(alAbrirAgente ? { durationMs: 8_000, action: { label: t('oficinaV2.contratarVentana.ver'), onClick: () => alAbrirAgente(contratado.agentId) } } : {}),
      });
    } catch (e) {
      setError(mensajeError(e));
      // Si el servidor contestó, aquel envío terminó: el siguiente intento es un encargo nuevo. Si no
      // contestó (red), se conserva la clave: el reintento puede ser el mismo envío que sí llegó.
      if (respondioElServidor(e)) clave.current = crypto.randomUUID();
    } finally {
      setEnviando(false);
    }
  }

  /** Los sube al agente recién contratado y se los manda en un mensaje; si alguno falla, se dice. */
  async function mandarAdjuntos(agentId: string, fs: File[]) {
    const subidas = await Promise.allSettled(
      fs.map(async (file) => {
        const chipId = crypto.randomUUID();
        await uploadAttachment({ agentId, chipId, messageId: crypto.randomUUID(), file });
        return chipId;
      }),
    );
    const listos = subidas.flatMap((x) => (x.status === 'fulfilled' ? [x.value] : []));
    try {
      // Recién contratado: el servidor confirma cuando el agente ya ha arrancado (hasta ~90 s preparando su copia).
      if (listos.length) await sendAgentInput(agentId, t('oficinaV2.contratarVentana.adjuntosMensaje'), listos, 120_000);
      if (listos.length < fs.length) useToastStore.getState().push({ message: t('oficinaV2.contratarVentana.adjuntosFallo', { n: fs.length - listos.length }) });
    } catch {
      useToastStore.getState().push({ message: t('oficinaV2.contratarVentana.adjuntosFallo', { n: fs.length }) });
    }
  }

  const pie = (
    <>
      <span className="mr-auto hidden text-o-sub text-oficina-texto3 sm:inline">{t('oficinaV2.contratarVentana.atajo')}</span>
      <Boton onClick={cerrar}>{t('oficinaV2.accion.cancelar')}</Boton>
      <Boton variante="primario" icono={UserPlus} disabled={!puede} cargando={enviando} onClick={() => void contratar()}>
        {t('oficinaV2.contratarVentana.contratarSinNombre')}
      </Boton>
    </>
  );

  return (
    <Ventana abierta={contratarAbierto} alCerrar={cerrar} ancho="lg" titulo={t('oficinaV2.contratarVentana.titulo')} descripcion={t('oficinaV2.contratarVentana.explica')} pie={pie}>
      <div
        className="space-y-6 pb-2"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void contratar();
        }}
      >
        {salas.length === 0 ? <Aviso tono="aviso">{t('oficinaV2.contratarVentana.sinSalas')}</Aviso> : null}

        {/* Crítica M4: el encargo es el protagonista; lo demás acompaña. */}
        <AreaTexto
          data-autofoco
          rows={7}
          value={b.caja}
          maxLength={LIMITES.caja}
          onChange={(e) => cambia('caja', e.target.value)}
          onPaste={(e) => {
            const fs = Array.from(e.clipboardData.files);
            if (fs.length) {
              e.preventDefault();
              anadir(fs);
            }
          }}
          onDrop={(e) => {
            const fs = Array.from(e.dataTransfer.files);
            if (fs.length) {
              e.preventDefault();
              anadir(fs);
            }
          }}
          placeholder={t('oficinaV2.contratarVentana.placeholder')}
          aria-label={t('oficinaV2.contratarVentana.titulo')}
          className="min-h-[168px]"
        />
        <div className="-mt-3 flex flex-wrap items-center gap-2">
          {archivos.map((a) => (
            <span key={a.id} className="relative flex h-14 items-center overflow-hidden rounded-o-s bg-white/[0.06] ring-1 ring-white/10">
              {a.vista ? (
                <img src={a.vista} alt={a.file.name} className="h-14 w-14 object-cover" />
              ) : (
                <span className="max-w-[160px] truncate px-3 text-o-sub text-oficina-texto2">{a.file.name}</span>
              )}
              <button
                type="button"
                onClick={() => quitar(a.id)}
                aria-label={t('oficinaV2.contratarVentana.quitarAdjunto', { nombre: a.file.name })}
                className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
          {archivos.length < MAX_ADJUNTOS ? (
            // 10-10 (punto 10): en el móvil el texto («…también puedes pegarlas aquí») se salía por la derecha; ahora baja de línea.
            <Boton variante="fantasma" tamano="sm" icono={Paperclip} onClick={() => elegir.current?.click()} className="h-auto min-h-9 max-w-full whitespace-normal py-1.5 text-left leading-snug [&>svg]:shrink-0">
              {t(archivos.length ? 'oficinaV2.contratarVentana.adjuntarMas' : 'oficinaV2.contratarVentana.adjuntar')}
            </Boton>
          ) : null}
          <input
            ref={elegir}
            type="file"
            multiple
            accept="image/*,application/pdf,.txt,.md,.csv,.json,.xlsx,.docx"
            className="hidden"
            onChange={(e) => {
              anadir(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
          />
        </div>
        {n.faltaMaterial ? <Aviso tono="aviso">{t('oficinaV2.contratarVentana.faltaMaterial')}</Aviso> : null}
        {secreto ? <Aviso tono="error">{t('oficinaV2.contratarVentana.secreto')}</Aviso> : null}

        <div className={cn('grid gap-4', salas.length > 1 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
          {salas.length > 1 ? (
            <Etiqueta titulo={t('oficinaV2.contratarVentana.sala')}>
              <Selector value={b.salaId} onChange={(e) => cambia('salaId', e.target.value)}>
                {salas.map((s) => (
                  <option key={s.projectId} value={s.projectId}>
                    {s.nombre}
                  </option>
                ))}
              </Selector>
            </Etiqueta>
          ) : null}
          <Etiqueta titulo={t('oficinaV2.contratarVentana.modelo')}>
            <Selector value={b.modelo} onChange={(e) => cambia('modelo', e.target.value)}>
              {MODELOS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nombre}
                </option>
              ))}
            </Selector>
          </Etiqueta>
          <Etiqueta titulo={t('oficinaV2.contratarVentana.potencia')}>
            <Selector value={b.potencia} onChange={(e) => cambia('potencia', e.target.value as Potencia)} title={t('oficinaV2.contratarVentana.potenciaAyuda')}>
              {POTENCIAS.map((p) => (
                <option key={p} value={p}>
                  {POTENCIA_EN_CRISTIANO[p]}
                </option>
              ))}
            </Selector>
          </Etiqueta>
        </div>

        <section className="rounded-o-m ring-1 ring-white/[0.08]">
          <button
            type="button"
            onClick={() => setDetalles(!detalles)}
            aria-expanded={detalles}
            className="flex w-full items-center justify-between gap-3 rounded-o-m px-4 py-3 text-left outline-none transition-colors hover:bg-white/[0.03] focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
          >
            <span>
              <span className="block text-o-cuerpo font-semibold text-oficina-texto">{t('oficinaV2.contratarVentana.contexto')}</span>
              <span className="block text-o-sub text-oficina-texto2">{t('oficinaV2.contratarVentana.contextoAyuda')}</span>
            </span>
            <ChevronDown className={cn('h-5 w-5 shrink-0 text-oficina-texto2 transition-transform', detalles && 'rotate-180')} aria-hidden />
          </button>
          {detalles ? (
            <div className="grid gap-4 border-t border-white/[0.06] p-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Etiqueta titulo={t('oficinaV2.contratarVentana.resultado')}>
                  <Campo value={b.resultado} maxLength={LIMITES.respuestaFormulario} onChange={(e) => cambia('resultado', e.target.value)} />
                </Etiqueta>
              </div>
              <div className="sm:col-span-2">
                <Etiqueta titulo={t('oficinaV2.contratarVentana.paraQue')}>
                  <Campo value={b.paraQue} maxLength={LIMITES.respuestaFormulario} onChange={(e) => cambia('paraQue', e.target.value)} />
                </Etiqueta>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Etiqueta titulo={t('oficinaV2.contratarVentana.comoSabre')}>
                  <Campo value={b.comoSabre} maxLength={LIMITES.respuestaFormulario} disabled={b.noLoSe} onChange={(e) => cambia('comoSabre', e.target.value)} />
                </Etiqueta>
                <Casilla marcada={b.noLoSe} alCambiar={(v) => cambia('noLoSe', v)}>
                  {t('oficinaV2.contratarVentana.noLoSe')}
                </Casilla>
              </div>
              <div className="sm:col-span-2">
                <Etiqueta titulo={t('oficinaV2.contratarVentana.noTocar')}>
                  <AreaTexto rows={2} maxLength={LIMITES.noTocar} value={b.noTocar} onChange={(e) => cambia('noTocar', e.target.value)} />
                </Etiqueta>
              </div>
              <Etiqueta titulo={t('oficinaV2.contratarVentana.tema')} ayuda={t('oficinaV2.contratarVentana.temaAyuda')}>
                <Campo value={b.tema} maxLength={LIMITES.tema} onChange={(e) => cambia('tema', e.target.value)} />
              </Etiqueta>
            </div>
          ) : null}
        </section>

        {error ? <Aviso tono="error">{error}</Aviso> : null}
      </div>
    </Ventana>
  );
}
